import React, { useState, useCallback, useContext, useEffect } from 'react';
import { Link } from 'gatsby';
import cn from 'classnames';
import { CSSTransition } from 'react-transition-group';

import Logo from '../../assets/logo.svg';
import Banner from '../../assets/gv_logo_banner.svg';
import SkipTo from '../SkipTo/SkipTo';
import LanguageSwitcher from '../LanguageSwitcher';
import MenuToggle from '../MenuToggle/MenuToggle';
import SearchToggle from '../SearchToggle/SearchToggle';
import SearchBar from '../SearchBar/SearchBar';
import OffCanvas from '../OffCanvas/OffCanvas';
import AccountButton from '../AccountButton/AccountButton';
import isExternalLink from '../../util/isExternalLink';
import { useTranslation } from 'react-i18next';
import MenuContext from '../../util/MenuContext'
import { ScrollContext } from '../../util/ScrollContext';

const Navigation = React.memo(({ language, urls, hideSearchOnScroll }) => {
  let defaultOpen = false;

  if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('digid-hamburger-open')) {
    defaultOpen = sessionStorage.getItem('digid-hamburger-open') == 'true';
  }

  const scrollContextState = useContext(ScrollContext)
  const [open, setOffCanvas] = useState(defaultOpen);
  const [openSearch, setSearchOverlay] = useState(false);
  const navigationRef = React.createRef()
  const { t } = useTranslation();
  const { top: links } = React.useContext(MenuContext)
  const defaultUrl = {
    'nl': '/',
    'en': '/en'
  }

  const contentClickHandler = () => {
    const displayValue = window.getComputedStyle(navigationRef.current).getPropertyValue('display');

    if (displayValue != 'none' && open) {
      setOffCanvas(false)
      setSearchOverlay(false)
    }

    window.history.replaceState(undefined, undefined, '#content')
  }

  const searchClickHandler = () => {
    if (!open) {
      setOffCanvas(true)
      setSearchOverlay(true)
    }

    window.history.replaceState(undefined, undefined, '#zoeken')
  }

  const navigationClickHandler = () => {
    const displayValue = window.getComputedStyle(navigationRef.current).getPropertyValue('display');

    if (displayValue != 'none' && !open) {
      setOffCanvas(true)
      setSearchOverlay(true)
    }
    window.history.replaceState(undefined, undefined, '#navigatie')
  }

  const homeUrl = defaultUrl[language.toLowerCase()];

  const useScrollDirection = () => {
    const [scrollDirection, setScrollDirection] = useState(null)

    useEffect(() => {
      let lastScrollY = window.pageYOffset

      const updateScrollDirection = () => {
        const scrollY = window.pageYOffset
        const direction = scrollY > lastScrollY ? "down" : "up"
        if (direction !== scrollDirection && (scrollY - lastScrollY > 5 || scrollY - lastScrollY < -5)) {
          setScrollDirection(direction)
        }
        lastScrollY = scrollY > 0 ? scrollY : 0
      }
      window.addEventListener("scroll", updateScrollDirection) // add event listener
      return () => {
        window.removeEventListener("scroll", updateScrollDirection) // clean up
      }
    }, [scrollDirection])
    return scrollDirection
  }

  const escHandler = useCallback(event => {
    if (event.key === 'Escape') {
      setOffCanvas(false)
      scrollContextState.toggleMenu(false)
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', escHandler)

    return () => {
      document.removeEventListener('keydown', escHandler)
    };
  }, [escHandler])

  const scrollDirection = useScrollDirection();

  return (
    <React.Fragment>
      <header className={cn('navigation', { 'off-canvas-open': open })}>
        <SkipTo text={t('skip_to.items.content')} hash="#content" clickHandler={contentClickHandler} />
        <SkipTo text={t('skip_to.items.navigation')} hash="#navigatie" clickHandler={navigationClickHandler} />
        <SkipTo text={t('skip_to.items.search')} extraClass="show-for-large-up" hash="#zoeken" clickHandler={searchClickHandler} />
        <div className="navigation-inner">
          <nav aria-labelledby="logo-nav-mobile" className="logo-nav">
            <h3 id="logo-nav-mobile" className="sr-only hide-for-large-up">Terug naar home</h3>
            <ul className="hide-for-large-up">
              <li>
                <Link to={homeUrl} title="Home">
                  <span className="visually-hidden">home</span>
                  <Logo className="logo" />
                </Link>
              </li>
            </ul>
          </nav>
          <div className="banner-nav">
            <ul>
              <li>
                <Banner className="banner" />
              </li>
            </ul>
          </div>
          <nav aria-labelledby="label-language-switcher" className="multilang-nav show-for-large-up">
            <h3 id="label-language-switcher" className="sr-only">Website taal veranderen</h3>
            <LanguageSwitcher language={language} urls={urls} />
          </nav>
          <CSSTransition in={open} timeout={450} classNames="menu-open">
            <nav aria-labelledby="label-language-switcher-mobile" className={`menu-nav hide-for-large-up ${open ? 'menu-open-enter-done' : ''}`} ref={navigationRef}>
              {/*<h3 id="label-language-switcher-mobile" className="sr-only">Website taal veranderen</h3>*/}
              {/*<LanguageSwitcher language={language} urls={urls} clickCallback={() => sessionStorage.setItem('digid-hamburger-open', true)}/>*/}
              <ul>
                <li className="show-for-medium-only">
                  <AccountButton text={t('navigation.my_digid')} url={t('my_digid_url')} />
                </li>
                <li>
                  <ScrollContext.Consumer>
                    {({ menuOpened, toggleMenu }) => (
                      <MenuToggle onClick={() => {
                        setOffCanvas(!open);
                        toggleMenu(!open);
                      }} open={open} />
                    )}
                  </ScrollContext.Consumer>
                </li>
              </ul>
            </nav>
          </CSSTransition>
          <div
            className="desktop-nav show-for-large-up"
            onKeyDown={(e) => {
              // Escape
              if (e.keyCode === 27) {
                setSearchOverlay(false)
              }
            }}
          >
            <nav aria-labelledby="label-navigatie" id="navigatie">
              <h3 id="label-navigatie" className="sr-only">Hoofdnavigatie</h3>
              <Link title="Home" to={homeUrl}>
                <Logo />
              </Link>
              <ul>
                <li>
                  <Link to={homeUrl} activeClassName="current">Home</Link>
                </li>
                {links && links.map(link => (
                  <li key={link.url.path}>
                    {isExternalLink(link.url.path)
                      ? <a href={link.url.path}>{link.label}</a>
                      : [
                        (typeof window !== 'undefined' && window.location.pathname.startsWith(link.url.path)
                          ? <Link to={link.url.path} activeClassName="current" key={link.url.path} aria-current="page" partiallyActive>{link.label}</Link>
                          : <Link to={link.url.path} activeClassName="current" key={link.url.path} partiallyActive>{link.label}</Link>
                        )
                      ]
                    }
                  </li>
                ))}
              </ul>
            </nav>
            <div className="nav-buttons">
              <ul>
                <li>
                  <AccountButton text={t('navigation.my_digid')} url={t('my_digid_url')} />
                </li>
                <li>
                  <ScrollContext.Consumer>
                    {({ menuOpened, toggleMenu }) => (
                        <SearchToggle
                            onClick={() => {
                              setSearchOverlay(!openSearch);
                              toggleMenu(!openSearch);
                            }}
                            open={openSearch}
                        />
                    )}
                  </ScrollContext.Consumer>
                </li>
              </ul>
            </div>
            <CSSTransition
              in={openSearch}
              timeout={300}
              classNames="searchbar"
            >
              <SearchBar setSearchOverlay={setSearchOverlay} openSearch={openSearch} />
            </CSSTransition>
          </div>
        </div>
        <CSSTransition
          in={open}
          unmountOnExit
          timeout={300}
          classNames="off-canvas"
        >
          <OffCanvas language={language} urls={urls} onClose={() => {
            setOffCanvas(false);
            setSearchOverlay(false);
          }} links={links} />
        </CSSTransition>
        <div className={`mobile-search-container hide-for-large-up ${ hideSearchOnScroll && scrollDirection === "down" ? "hide" : "show"}`}>
          <SearchBar setSearchOverlay={setSearchOverlay} openSearch={openSearch} autoFocus={false} isMobile />
        </div>
      </header>
    </React.Fragment>
  );
});

export default Navigation;

